import { Recipe, createRecipe, searchIngredients, createIngredient } from '../api/index.js';
import type { Ingredient, Quantity } from '../api/types.js';
import { unitCache } from '../unit-cache.js';
import { getDefaultImageUrl } from '../recipe_renderer/imageUtils.js';

/**
 * Dialog for creating a new recipe from the home page.
 * Includes recipe name, description, ingredient search + select, and quantity.
 * Returns a Promise that resolves with the created Recipe or null if cancelled.
 */
export class RecipeCreationDialog {
  private modalElement: HTMLElement | null = null;
  private resolvePromise: ((value: Recipe | null) => void) | null = null;
  private selectedIngredient: Ingredient | null = null;
  private dropdown: HTMLElement | null = null;
  private searchTimeout: number | null = null;
  private destroyInput: (() => void) | null = null;
  private destroyOutsideClick: (() => void) | null = null;
  private lastResults: Ingredient[] = [];

  /**
   * Open the dialog and return a promise that resolves when user submits or cancels.
   * Ensures the unit cache is loaded before showing the form.
   */
  async open(): Promise<Recipe | null> {
    // Ensure unit cache is loaded for the quantity unit select
    try {
      await unitCache.ensureLoaded();
    } catch {
      // Continue even if cache fails — unit select will just be empty
    }

    return new Promise((resolve) => {
      this.resolvePromise = resolve;
      this.createModal();
    });
  }

  private createModal(): void {
    this.modalElement = document.createElement('div');
    this.modalElement.className =
      'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
    this.modalElement.id = 'recipe-creation-dialog';

    const content = document.createElement('div');
    content.className =
      'bg-[#1e1e2e] rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-[var(--accent-alpha-20)]';

    // Header
    const header = document.createElement('h2');
    header.className = 'text-xl font-semibold mb-4 text-[var(--accent)]';
    header.textContent = 'Nova recepta';
    content.appendChild(header);

    // Form
    const form = document.createElement('form');
    form.className = 'space-y-4';
    form.id = 'recipe-creation-form';

    // Name (required)
    const nameGroup = document.createElement('div');
    nameGroup.innerHTML = `
      <label for="recipe-name-input" class="block text-sm font-medium text-[#a0a0a0] mb-1">
        Nom <span class="text-red-400">*</span>
      </label>
      <input
        type="text"
        id="recipe-name-input"
        name="name"
        required
        class="w-full px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
        placeholder="p. ex., Truita de patates"
      />
    `;
    form.appendChild(nameGroup);

    // Description (optional)
    const descGroup = document.createElement('div');
    descGroup.innerHTML = `
      <label for="recipe-desc-input" class="block text-sm font-medium text-[#a0a0a0] mb-1">
        Descripció
      </label>
      <textarea
        id="recipe-desc-input"
        name="description"
        rows="2"
        class="w-full px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
        placeholder="Descripció opcional..."
      ></textarea>
    `;
    form.appendChild(descGroup);

    // Ingredient search
    const ingGroup = document.createElement('div');
    ingGroup.innerHTML = `
      <label class="block text-sm font-medium text-[#a0a0a0] mb-1">
        Ingredient principal (opcional)
      </label>
    `;
    const searchWrapper = document.createElement('div');
    searchWrapper.className = 'relative';

    const ingInput = document.createElement('input');
    ingInput.type = 'text';
    ingInput.id = 'creation-ingredient-input';
    ingInput.className =
      'w-full px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    ingInput.placeholder = 'Cerca un ingredient...';
    searchWrapper.appendChild(ingInput);

    const dropdown = document.createElement('div');
    dropdown.id = 'creation-ingredient-dropdown';
    // max-h-[180px] = 5 * the row height (h-9, see showDropdown()) — shows
    // ~5 results, the rest reachable by scrolling rather than cut off.
    dropdown.className =
      'absolute z-20 w-full mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md text-[#e0e0e0] overflow-y-auto max-h-[180px] shadow-lg';
    dropdown.style.display = 'none';
    searchWrapper.appendChild(dropdown);
    this.dropdown = dropdown;

    ingGroup.appendChild(searchWrapper);

    // Selected ingredient chip
    const selectedChip = document.createElement('div');
    selectedChip.id = 'creation-selected-ingredient';
    selectedChip.className = 'hidden mt-2';
    ingGroup.appendChild(selectedChip);

    form.appendChild(ingGroup);

    // Quantity (optional)
    const qtyGroup = document.createElement('div');
    qtyGroup.innerHTML = `
      <label class="block text-sm font-medium text-[#a0a0a0] mb-1">
        Quantitat (opcional)
      </label>
      <div class="flex gap-2 items-center">
        <input
          type="number"
          id="recipe-qty-amount"
          name="qtyAmount"
          step="0.01"
          min="0"
          class="flex-1 min-w-0 px-3 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          placeholder="Quantitat"
        />
        <select
          id="recipe-qty-unit"
          class="flex-none px-2 py-2 bg-[#0d1117] border border-[#333] rounded-md text-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
        >
          <option value="">—</option>
        </select>
      </div>
    `;
    form.appendChild(qtyGroup);

    // Populate unit select
    this.populateUnitSelect();

    // Error message area
    const errorDiv = document.createElement('div');
    errorDiv.id = 'recipe-creation-error';
    errorDiv.className = 'text-red-400 text-sm hidden';
    form.appendChild(errorDiv);

    // Buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'flex gap-3 pt-2';

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.id = 'recipe-create-btn';
    submitBtn.className =
      'flex-1 bg-[var(--accent)] text-white font-semibold px-4 py-2 rounded-md hover:bg-[var(--accent-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
    submitBtn.textContent = 'Crea';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.id = 'recipe-cancel-btn';
    cancelBtn.className =
      'flex-1 bg-[#333] text-[#e0e0e0] px-4 py-2 rounded-md hover:bg-[#444] focus:outline-none focus:ring-2 focus:ring-[#555]';
    cancelBtn.textContent = 'Cancel·la';

    buttonGroup.appendChild(submitBtn);
    buttonGroup.appendChild(cancelBtn);
    form.appendChild(buttonGroup);

    content.appendChild(form);
    this.modalElement.appendChild(content);

    // Event handlers
    form.addEventListener('submit', (e) => this.handleSubmit(e));
    cancelBtn.addEventListener('click', () => this.close(null));
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.close(null);
    });

    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close(null);
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);

    document.body.appendChild(this.modalElement);

    // Wire ingredient search
    this.setupIngredientSearch(ingInput);

    // Focus the name input
    const nameInput = document.getElementById('recipe-name-input') as HTMLInputElement;
    if (nameInput) nameInput.focus();
  }

  private populateUnitSelect(): void {
    const select = document.getElementById('recipe-qty-unit') as HTMLSelectElement;
    if (!select) return;

    // Add empty option
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = '—';
    select.appendChild(emptyOption);

    // Populate from unit cache
    const allUnits = unitCache.getAll();
    if (allUnits.length > 0) {
      allUnits.forEach(unit => {
        const option = document.createElement('option');
        option.value = unit.id;
        option.textContent = unit.plural;
        select.appendChild(option);
      });
    }
  }

  private setupIngredientSearch(input: HTMLInputElement): void {
    const inputHandler = (e: Event) => {
      const query = (e.target as HTMLInputElement).value.trim();
      if (this.searchTimeout !== null) {
        window.clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = window.setTimeout(() => {
        if (query.length > 0) {
          this.performSearch(query);
        } else {
          this.hideDropdown();
        }
      }, 300);
    };
    input.addEventListener('input', inputHandler);
    this.destroyInput = () => input.removeEventListener('input', inputHandler);

    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hideDropdown();
      }
    };
    input.addEventListener('keydown', keydownHandler);

    // Click outside to close dropdown
    const outsideClickHandler = (e: MouseEvent) => {
      const wrapper = input.closest('.relative');
      if (wrapper && !wrapper.contains(e.target as Node)) {
        this.hideDropdown();
      }
    };
    document.addEventListener('click', outsideClickHandler);
    this.destroyOutsideClick = () => document.removeEventListener('click', outsideClickHandler);
  }

  private async performSearch(query: string): Promise<void> {
    try {
      // Fetch more than the ~5 that fit in the dropdown's fixed visible
      // height (see showDropdown()) — some queries genuinely match many
      // ingredients, reachable by scrolling instead of just cut off.
      this.lastResults = await searchIngredients(query, 20);
      this.showDropdown();
    } catch (error) {
      console.error('Failed to search ingredients:', error);
      this.lastResults = [];
      this.showDropdown();
    }
  }

  private showDropdown(): void {
    if (!this.dropdown) return;

    this.dropdown.innerHTML = '';

    const available = this.lastResults.filter(
      (ing) => !this.selectedIngredient || ing.id !== this.selectedIngredient.id
    );

    const showCreateOption = available.length === 0 && this.lastResults.length === 0;

    if (showCreateOption) {
      // No results — show "Create ingredient" option
      const input = document.getElementById('creation-ingredient-input') as HTMLInputElement;
      const query = input?.value.trim() || '';
      if (query) {
        const createRow = document.createElement('div');
        createRow.className =
          'px-4 py-2 text-sm hover:bg-[var(--accent-alpha-13)] cursor-pointer transition-colors text-[var(--accent)] font-bold';
        createRow.textContent = `+ Crea "${query}"`;
        createRow.addEventListener('click', () => {
          this.createNewIngredient(query);
          this.hideDropdown();
        });
        this.dropdown.appendChild(createRow);
      }
    } else if (available.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'px-4 py-2 text-[#888] text-sm';
      noResults.textContent = 'No s\'han trobat ingredients';
      this.dropdown.appendChild(noResults);
    } else {
      for (const ing of available) {
        const row = document.createElement('div');
        row.className =
          'h-9 px-4 py-2 text-sm hover:bg-[var(--accent-alpha-13)] cursor-pointer transition-colors flex items-center gap-2';

        const ingImageUrl = getDefaultImageUrl(ing.images);
        if (ingImageUrl) {
          const img = document.createElement('img');
          img.src = ingImageUrl;
          img.alt = ing.name;
          img.className = 'rounded-full';
          img.style.cssText = 'width: 20px; height: 20px; object-fit: cover; flex-shrink: 0;';
          row.appendChild(img);
        }

        const nameSpan = document.createElement('span');
        nameSpan.textContent = ing.name;
        row.appendChild(nameSpan);

        row.addEventListener('click', () => {
          this.selectIngredient(ing);
          this.hideDropdown();
          const ingInput = document.getElementById('creation-ingredient-input') as HTMLInputElement;
          if (ingInput) ingInput.value = '';
        });
        this.dropdown.appendChild(row);
      }
    }

    this.dropdown.style.display = 'block';
  }

  private hideDropdown(): void {
    if (this.dropdown) {
      this.dropdown.style.display = 'none';
    }
  }

  private selectIngredient(ingredient: Ingredient): void {
    this.selectedIngredient = ingredient;
    const chip = document.getElementById('creation-selected-ingredient');
    if (!chip) return;
    chip.className = 'mt-2';
    chip.innerHTML = `
      <span class="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--accent-alpha-13)] border border-[var(--accent-alpha-27)] rounded-full text-[var(--accent)] text-sm font-mono">
        ${getDefaultImageUrl(ingredient.images)
          ? `<img src="${getDefaultImageUrl(ingredient.images)}" alt="" class="rounded-full" style="width:18px;height:18px;object-fit:cover;flex-shrink:0;" />`
          : ''}
        <span>${this.escapeHtml(ingredient.name)}</span>
        <button type="button" class="ml-1 text-[var(--accent)] hover:text-red-400 font-bold leading-none text-xs" title="Elimina">×</button>
      </span>
    `;
    const removeBtn = chip.querySelector('button');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.selectedIngredient = null;
        chip.className = 'hidden';
      });
    }
  }

  private async createNewIngredient(name: string): Promise<void> {
    try {
      const ingredient = await createIngredient({ name });
      this.selectIngredient(ingredient);
    } catch (error) {
      console.error('Failed to create ingredient:', error);
    }
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const submitBtn = document.getElementById('recipe-create-btn') as HTMLButtonElement;
    const errorDiv = document.getElementById('recipe-creation-error');

    const name = (formData.get('name') as string || '').trim();
    const description = (formData.get('description') as string || '').trim();
    const qtyAmountStr = formData.get('qtyAmount') as string || '';
    const qtyUnit = (document.getElementById('recipe-qty-unit') as HTMLSelectElement)?.value || '';

    if (!name) {
      if (errorDiv) {
        errorDiv.textContent = 'El nom és obligatori';
        errorDiv.classList.remove('hidden');
      }
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Creant...';
    }
    this.hideError();

    // Build quantity if both amount and unit are provided
    let quantity: Quantity | undefined;
    if (qtyAmountStr && qtyUnit) {
      const amount = parseFloat(qtyAmountStr);
      if (!isNaN(amount) && amount > 0) {
        quantity = { amount, unit: qtyUnit };
      }
    }

    try {
      const created = await createRecipe({
        name,
        description: description || undefined,
        ingredientId: this.selectedIngredient?.id,
        quantity,
      });
      this.close(created);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "No s'ha pogut crear la recepta";
      if (errorDiv) {
        errorDiv.textContent = msg;
        errorDiv.classList.remove('hidden');
      }
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Crea';
      }
    }
  }

  private showError(message: string): void {
    const errorDiv = document.getElementById('recipe-creation-error');
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.classList.remove('hidden');
    }
  }

  private hideError(): void {
    const errorDiv = document.getElementById('recipe-creation-error');
    if (errorDiv) {
      errorDiv.textContent = '';
      errorDiv.classList.add('hidden');
    }
  }

  private close(result: Recipe | null): void {
    // Cleanup event listeners
    if (this.destroyInput) this.destroyInput();
    if (this.destroyOutsideClick) this.destroyOutsideClick();
    if (this.searchTimeout !== null) {
      window.clearTimeout(this.searchTimeout);
    }

    if (this.modalElement && this.modalElement.parentNode) {
      this.modalElement.parentNode.removeChild(this.modalElement);
    }
    this.modalElement = null;

    if (this.resolvePromise) {
      this.resolvePromise(result);
      this.resolvePromise = null;
    }
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
